import { fetchSiteConfig } from "./core/config";
import {
  initContextFromWindow,
  setAgentContext,
  setConsent,
  shouldPersistIdentity,
} from "./core/context";
import { buildEvent } from "./core/event_factory";
import { ensureSessionId, ensureVisitorId, newPageInstanceId, touchSession } from "./core/identity";
import { EventQueue, type FlushReason } from "./core/queue";
import { send, flushPending } from "./core/sender";
import { buildPageViewProps } from "./events/page_view";
import { installClickTracker } from "./events/clicks";
import { installFormTracker } from "./events/forms";
import { installScrollTracker, type ScrollTracker } from "./events/scroll";
import { installEngagementTracker, type EngagementTracker } from "./events/engagement";
import { installWebVitals, type WebVitalsTracker } from "./events/web_vitals";
import { scheduleInitialPageScan, runPageScan } from "./events/page_scan";
import { installSpaNavigation } from "./events/spa";
import { installTildaFormHooks } from "./events/tilda_forms";
import { installChatWidgetHooks } from "./events/chat_widgets";
import type { Json, PixelAPI, PixelEvent, SiteConfig } from "./types";

const FLUSH_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;

const PIXEL_SRC_RE = /(^|\/)pixel(\.min)?\.js$/;

/**
 * Where the site key comes from, in priority order:
 *   1. `data-x-site-id` on any <script> (the documented snippet);
 *   2. the pixel script's own URL: `pixel.js?site=site_xxx` (also `site_id`, `id`).
 *      Tag managers re-create <script> elements when injecting Custom HTML and
 *      keep only `src` — Google Tag Manager dropped `data-x-site-id` on
 *      evrosfera.ru (2026-09-24), so the pixel loaded and silently did nothing;
 *   3. `window.XPixelSiteId` set by the page before the script runs.
 */
function readSiteIdFromScript(): { siteId: string | null; baseUrl: string } {
  const scripts = document.getElementsByTagName("script");
  let fromSrc: { siteId: string; baseUrl: string } | null = null;
  for (let i = scripts.length - 1; i >= 0; i--) {
    const s = scripts[i];
    const src = s.getAttribute("src") || "";
    let u: URL | null = null;
    try {
      u = src ? new URL(src, location.href) : null;
    } catch {
      u = null;
    }
    const baseUrl = u ? u.origin : "";
    const attr = s.getAttribute("data-x-site-id");
    if (attr) return { siteId: attr, baseUrl };
    if (!fromSrc && u && PIXEL_SRC_RE.test(u.pathname)) {
      const v = u.searchParams.get("site") || u.searchParams.get("site_id") || u.searchParams.get("id");
      if (v) fromSrc = { siteId: v, baseUrl };
    }
  }
  if (fromSrc) return fromSrc;
  const fromWindow = window.XPixelSiteId;
  if (typeof fromWindow === "string" && fromWindow) return { siteId: fromWindow, baseUrl: "" };
  return { siteId: null, baseUrl: "" };
}

function pathIgnored(path: string, ignored: string[]): boolean {
  for (const p of ignored) {
    if (p && path.indexOf(p) === 0) return true;
  }
  return false;
}

(function init() {
  if (window.XPixel) return;
  if (typeof document === "undefined") return;

  const { siteId: siteIdMaybe, baseUrl: scriptBase } = readSiteIdFromScript();
  if (!siteIdMaybe) return;
  const siteId: string = siteIdMaybe;

  // __PIXEL_COLLECTOR_BASE_URL__ is inlined by esbuild at build time. In
  // split CDN+API deployments (static.X / collector.X) it MUST be set so the
  // pixel doesn't try to call the API on the static host. When unset (local
  // dev), we fall back to the script's own origin — correct for
  // http://localhost:8000 where everything lives.
  const buildTimeCollector = (__PIXEL_COLLECTOR_BASE_URL__ || "").replace(/\/$/, "");
  const collectorBase = buildTimeCollector || scriptBase || location.origin;

  // Prerendered / background-tab loads (Yandex Browser and Yandex search
  // prefetch the top result, Chrome prerenders) execute scripts before the
  // user ever sees the page. On hellopark-avk.ru that was 67 "desktop from
  // yandex.ru" sessions with no LCP, no engagement, no scroll. Don't start
  // until the page is actually visible.
  startWhenVisible(boot);

  function boot(): void {
  initContextFromWindow();

  let config: SiteConfig | null = null;
  let visitorId: string | null = null;
  let sessionId = "";
  let pageInstanceId = newPageInstanceId();
  let pageViewSent = false;

  const queue = new EventQueue({
    flushSize: FLUSH_SIZE,
    flushIntervalMs: FLUSH_INTERVAL_MS,
    onFlush: (events: PixelEvent[], reason: FlushReason) => {
      void send({ endpoint: collectorBase + "/v1/events/batch", siteId }, events, reason === "visibility");
    },
  });

  function getConfig(): SiteConfig | null {
    return config;
  }

  function track(eventName: string, props?: Record<string, Json>): void {
    if (!config) return;
    if (config.ignore_paths && pathIgnored(location.pathname, config.ignore_paths)) return;
    if (eventName === "page_view" && !config.tracking.page_views) return;

    const event = buildEvent({
      eventName,
      visitorId,
      sessionId,
      pageInstanceId,
      props: props ?? {},
    });
    queue.enqueue(event);
    touchSession();
  }

  function emitPageView(isSpa: boolean): void {
    if (!isSpa && pageViewSent) return;
    if (!isSpa) pageViewSent = true;
    track("page_view", buildPageViewProps(isSpa));
  }

  const api: PixelAPI = {
    track,
    consent(state) {
      setConsent(state);
      if (config && shouldPersistIdentity(config.consent_mode) && visitorId === null) {
        visitorId = ensureVisitorId(true);
      }
    },
    setContext(ctx) {
      setAgentContext(ctx);
    },
  };

  window.XPixel = api;

  // Subsystems whose lifetime spans navigations.
  let scrollTracker: ScrollTracker | null = null;
  let engagementTracker: EngagementTracker | null = null;
  let webVitalsTracker: WebVitalsTracker | null = null;

  fetchSiteConfig(collectorBase, siteId).then((cfg) => {
    if (!cfg) return;
    config = cfg;

    const persist = shouldPersistIdentity(cfg.consent_mode);
    visitorId = ensureVisitorId(persist);
    const session = ensureSessionId();
    sessionId = session.sessionId;

    if (session.isNew) {
      track("session_start", { reason: "new_session" });
    }

    emitPageView(false);

    scheduleInitialPageScan({ emit: track, getConfig });

    scrollTracker = installScrollTracker({ emit: track, getConfig });
    engagementTracker = installEngagementTracker({
      emit: track,
      getMaxScrollDepth: () => (scrollTracker ? scrollTracker.getMaxScrollDepth() : 0),
    });
    webVitalsTracker = installWebVitals({ emit: track, getConfig });

    installClickTracker({
      emit: track,
      getConfig,
      getEngagedMs: () => (engagementTracker ? engagementTracker.getEngagedMs() : 0),
      getScrollDepth: () => (scrollTracker ? scrollTracker.getMaxScrollDepth() : 0),
    });
    installFormTracker({ emit: track, getConfig });
    installTildaFormHooks({ emit: track });
    installChatWidgetHooks({ emit: track });

    installSpaNavigation({
      onNavigate: () => {
        // Flush engagement + reset per-page state, then emit a fresh page_view.
        engagementTracker?.emitNow("spa");
        engagementTracker?.reset();
        scrollTracker?.reset();
        pageInstanceId = newPageInstanceId();
        emitPageView(true);
        try {
          runPageScan({ emit: track, getConfig });
        } catch {
          // ignore
        }
      },
    });
  });

  const handleVisibility = () => {
    if (document.visibilityState === "hidden") {
      flushPending();
      engagementTracker?.emitNow("visibility");
      webVitalsTracker?.emitFinal();
      queue.flush("visibility");
    }
  };
  document.addEventListener("visibilitychange", handleVisibility);
  window.addEventListener("pagehide", () => {
    flushPending();
    engagementTracker?.emitNow("visibility");
    webVitalsTracker?.emitFinal();
    queue.flush("visibility");
  });
  }
})();

function startWhenVisible(fn: () => void): void {
  const prerendering = Boolean((document as unknown as { prerendering?: boolean }).prerendering);
  if (document.visibilityState === "visible" && !prerendering) {
    fn();
    return;
  }
  const onChange = () => {
    const stillPrerendering = Boolean((document as unknown as { prerendering?: boolean }).prerendering);
    if (document.visibilityState !== "visible" || stillPrerendering) return;
    document.removeEventListener("visibilitychange", onChange);
    document.removeEventListener("prerenderingchange", onChange);
    fn();
  };
  document.addEventListener("visibilitychange", onChange);
  document.addEventListener("prerenderingchange", onChange);
}
